import { Link } from 'react-router-dom';
import { Badge } from '../components/common';

export default function OperationsPage() {
  return (
    <>
      <div className="page-head">
        <div>
          <h1>How configuration works</h1>
          <p>
            Centralized DSP is split into three function domains. Each domain lists its Lambdas;
            opening a function shows exactly what it does for every DSP parent.
          </p>
        </div>
      </div>

      <div className="grid" style={{ marginBottom: 18 }}>
        <DomainCard
          to="/settlement"
          title="1. Settlement Operations"
          body="Ready-to-settle → settlement request load → confirmation receipts → funding receipts → escrow sweep. Per-DSP buckets, Zoho Partner ids, Snowflake DS/escrow profiles."
        />
        <DomainCard
          to="/lead-scoring"
          title="2. Lead Scoring"
          body="Eligibility export, LendAPI raw export/score write-back, Zoho lead load from client/creditor tables. A missing binding means that DSP is skipped."
        />
        <DomainCard
          to="/general"
          title="3. General Lambdas"
          body="Shared tooling — Data View, Zoho schema, Snowflake query/refresh — not owned by one pipeline but still profile-aware where relevant."
        />
      </div>

      <div className="card">
        <h2>Control plane → runtime</h2>
        <ol className="steps">
          <li>
            <strong>Edit</strong> DSP parents and per-function bindings in this admin (DynamoDB).
          </li>
          <li>
            <strong>Validate &amp; publish</strong> →{' '}
            <code>s3://skybridge-dsp-config/published/latest.json</code>.
          </li>
          <li>
            <strong>Lambdas refresh</strong> via vendored <code>skybridge_dsp_config</code> (~5 min
            TTL), fail closed on unknown partners, never merge old Debt Blue defaults.
          </li>
        </ol>
        <div className="actions" style={{ marginTop: 12 }}>
          <Link className="btn primary" to="/matrix">
            Open config matrix
          </Link>
          <Link className="btn" to="/publish">
            Validate &amp; publish
          </Link>
        </div>
      </div>

      <div className="card">
        <h2>Inside a function page — what you should see</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Section</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1 · What this function does</td>
                <td>Estate-wide behaviour and sequence (catalog).</td>
              </tr>
              <tr>
                <td>2 · What it does for each DSP</td>
                <td>
                  Per-parent narrative: enabled?, bucket, file types, Zoho ids, Snowflake profile,
                  resolved S3 keys, webhook/extra, and the client calls used at runtime.
                </td>
              </tr>
              <tr>
                <td>3–5 · Trigger / I/O / failure</td>
                <td>How it is invoked and how it fails closed.</td>
              </tr>
              <tr>
                <td>6 · Edit bindings</td>
                <td>Change what will be published for each parent; then publish.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>Settlement path (happy path)</h2>
        <ol className="steps">
          <li>
            <Link to="/lambdas/sm1_loans_ready_to_settle">SM1</Link> writes ready-to-settle into each
            enabled DSP bucket.
          </li>
          <li>
            DSP returns a settlement-request →{' '}
            <Link to="/lambdas/sm2_load_settlement_feed">SM2</Link> loads Zoho for that parent only.
          </li>
          <li>
            <Link to="/lambdas/sm6_settlement_confirmation_receipt">SM6-receipt</Link> writes
            settlement-confirmation (Clarity may webhook).
          </li>
          <li>
            <Link to="/lambdas/sm6_create_funds_confirmation">SM6-create</Link> builds funding-receipt
            (source/output file types from binding <code>extra</code>).
          </li>
          <li>
            <Link to="/lambdas/sm6_process_funds_received">SM6-process</Link> consumes funding-receipt
            drops.
          </li>
          <li>
            <Link to="/lambdas/sm10_escrow_sweep">SM10</Link> /{' '}
            <Link to="/lambdas/true_escrow_balance">true-escrow</Link> handle sweep and balances via
            that parent&apos;s Snowflake profile.
          </li>
        </ol>
        <div className="chips" style={{ marginTop: 8 }}>
          {['sm1_loans_ready_to_settle', 'sm2_load_settlement_feed', 'sm6_process_funds_received', 'sm10_escrow_sweep'].map(
            (key) => (
              <Link key={key} to={`/lambdas/${key}`}>
                <Badge tone="danger">{key}</Badge>
              </Link>
            ),
          )}
          <span className="small muted">required for active settlement parents</span>
        </div>
      </div>

      <div className="card">
        <h2>Day-to-day</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Task</th>
                <th>Where</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>See wiring across the estate</td>
                <td>
                  <Link to="/matrix">Config matrix</Link> (filter by domain mentally via function
                  list pages)
                </td>
              </tr>
              <tr>
                <td>Understand one function for one DSP</td>
                <td>
                  Domain list → function → section 2 “What it does for each DSP”
                </td>
              </tr>
              <tr>
                <td>Change routing</td>
                <td>Function section 6 → save → <Link to="/publish">publish</Link></td>
              </tr>
              <tr>
                <td>Add operator</td>
                <td>
                  <Link to="/users">Users &amp; audit</Link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function DomainCard({ to, title, body }: { to: string; title: string; body: string }) {
  return (
    <Link className="stat" to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="n" style={{ fontSize: 16, lineHeight: 1.35 }}>
        {title}
      </div>
      <div className="l" style={{ marginTop: 8, whiteSpace: 'normal' }}>
        {body}
      </div>
    </Link>
  );
}
